import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  CoachingDetailsInput,
  CoachingFormInputData,
  CoachingTopicObject,
  defaultCoachingDetailsValues,
  enumEmpty,
  VerticalType,
} from '../../../api/CoachingForm/schema';
import validationSchema from './Context';
import { getBehaviors, getMetrics, getTenets, getTopics } from './Data';
import * as FormField from './FormFields';
import { StateContext } from '../../../GlobalStateStore';
import { useContext } from 'react';

interface SubformContentProps extends FormikProps<CoachingDetailsInput> {
  contactReview: CoachingFormInputData['contactReview'];
  userId: string;
}

const SubformContent: React.FC<SubformContentProps> = ({
  values,
  errors,
  touched,
  handleSubmit,
  handleReset,
  setFieldValue,
  setValues,
  contactReview,
  userId,
}: SubformContentProps): React.ReactElement => {
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState?.state;
  const requesterOwner = inspireOptions.data.requesterOwner;
  const intl = useIntl();

  const reviewedContactIDs = contactReview.map(review => review.cscContactID);
  const contactOptionValues = reviewedContactIDs.filter(
    (contactID, index) => reviewedContactIDs.indexOf(contactID) === index
  );

  const showContacts: boolean = values.coachingTopic !== enumEmpty;
  const contactOptional: boolean =
    values.coachingTopic === CoachingTopicObject.COMPLIANCE ||
    values.coachingTopic === CoachingTopicObject.PRODUCTIVITY;
  const contactSelected: boolean =
    !!values.relatedContacts && values.relatedContacts.length > 0;
  const contactReviewsExist: boolean =
    !!contactOptionValues && contactOptionValues.length > 0;
  const developmentTopic: boolean =
    values.coachingTopic === CoachingTopicObject.DEVELOPMENT;
  const tenetLimitReached: boolean =
    !!values.tenets && values.tenets.length >= 2;
  const behaviourLimitReached: boolean =
    !!values.behaviors && values.behaviors.length >= 3;

  const selectedVerticals: VerticalType[] = values.relatedContacts
    .map(
      contact =>
        contactReview.find(review => review.cscContactID === contact)?.vertical
    )
    .filter(
      (vertical): vertical is VerticalType =>
        vertical !== enumEmpty && vertical !== undefined
    );

  return (
    <Form>
      <Column spacing='400'>
        <FormField.Topic
          values={values.coachingTopic}
          options={getTopics(intl)}
          update={(props: {
            target: { name: string; value: string };
          }): void => {
            const { name, value } = props.target;
            if (value === 'DEVELOPMENT') {
              setValues(defaultCoachingDetailsValues);
              setFieldValue('assignedAction', values.assignedAction);
            }
            setFieldValue(name, value);
          }}
          conditions={{ contactReviewsExist }}
          touched={touched.coachingTopic}
          error={errors.coachingTopic}
        />

        <FormField.Contact
          values={values.relatedContacts}
          options={contactOptionValues.map(contact => {
            return { label: contact, value: contact };
          })}
          conditions={{
            developmentTopic,
            showContacts,
            contactOptional,
          }}
          touched={touched.relatedContacts}
          error={errors.relatedContacts}
        />

        <FormField.Tenets
          values={values.tenets}
          options={getTenets(intl)}
          conditions={{
            developmentTopic,
            contactSelected,
            tenetLimitReached,
            contactOptional,
          }}
          touched={touched.tenets}
          error={errors.tenets}
        />

        <FormField.Behaviors
          values={values.behaviors}
          options={getBehaviors(intl, selectedVerticals)}
          conditions={{
            developmentTopic,
            contactSelected,
            behaviourLimitReached,
            contactOptional,
          }}
          touched={touched.behaviors}
          error={errors.behaviors}
        />

        <FormField.Metric
          values={values.relatedMetric}
          options={getMetrics(
            requesterOwner,
            values.coachingTopic,
            selectedVerticals,
            values.relatedMetric
          )}
          conditions={{
            developmentTopic,
            contactSelected,
            contactOptional,
          }}
          touched={touched.relatedMetric}
          error={errors.relatedMetric}
        />

        <FormField.Action
          values={values.assignedAction}
          conditions={{
            developmentTopic,
            contactSelected,
            contactOptional,
          }}
          touched={touched.assignedAction}
          error={errors.assignedAction}
        />
      </Column>

      <Row alignmentHorizontal='end' spacingInset='400'>
        <Button
          data-csa-c-type='link'
          data-csa-c-slot-id='coaching-details-subform-slot'
          data-csa-c-content-id='coaching-details-subform-cancel-button'
          onClick={handleReset}
          type='link'
        >
          <FormattedMessage id='cs_perfportal_cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          data-csa-c-type='link'
          data-csa-c-slot-id='coaching-details-subform-slot'
          data-csa-c-content-id='coaching-details-subform-save-button'
          data-cy='save-continue-coaching-details'
          onClick={handleSubmit}
          type='tertiary'
        >
          <FormattedMessage
            id='cs_perfportal_coaching_save_and_continue'
            defaultMessage='Save & continue'
            description='Save and continue button'
          />
        </Button>
      </Row>
    </Form>
  );
};

interface SubformProps {
  onsubmit: Function;
  onreset: () => void;
  initialValues: CoachingDetailsInput;
  userId: string;
}

const Subform = ({
  onsubmit,
  onreset,
  initialValues,
  userId,
}: SubformProps): React.ReactElement => {
  const intl = useIntl();
  const handleSubmit = (values: CoachingDetailsInput): void => onsubmit(values);

  const {
    values: { contactReview },
  }: FormikProps<CoachingFormInputData> = useFormikContext();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(intl)}
      onSubmit={handleSubmit}
      onReset={onreset}
    >
      {(props: FormikProps<CoachingDetailsInput>): React.ReactElement => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <SubformContent
          {...props}
          contactReview={contactReview}
          userId={userId}
        />
      )}
    </Formik>
  );
};

export default Subform;
